/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  useForm,
  FormProvider,
  SubmitHandler,
  Controller,
} from "react-hook-form";
import { z, ZodEffects, ZodObject, ZodTypeAny } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import TextField from "../TextField/TextField";
import styles from "./DynamicFrom.module.scss";
import { useEffect } from "react";
import CheckBox from "../CheckBox/CheckBox";

export type FieldType =
  | "text"
  | "password"
  | "select"
  | "email"
  | "multi-select"
  | "text-area";
export type TextFieldType =
  | "text"
  | "password"
  | "email"
  | "text-area"
  | "number";

export interface FormFieldConfig {
  name: string;
  label?: string;
  type: string;
  placeholder?: string;
  options?: { label: string; value: string }[];
  inputs?: FormFieldConfig[];
  validation?: ZodTypeAny;
  flexGrow?: number;
  hidden?: (formData: Record<string, any>) => boolean;
}

interface DynamicFormProps {
  id: string;
  fields: FormFieldConfig[];
  onSubmit: SubmitHandler<Record<string, any>>;
  validationSchema?: ZodObject<any> | ZodEffects<ZodObject<any>>;
  setButtonDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
  condensed?: boolean;
}

function DynamicForm({
  id,
  fields,
  onSubmit,
  validationSchema,
  setButtonDisabled,
  condensed,
}: DynamicFormProps) {
  const baseSchema = z.object(
    fields.reduce(
      (acc, field) => {
        acc[field.name] = field.validation || z.any();
        return acc;
      },
      {} as Record<string, ZodTypeAny>,
    ),
  );

  const schema = validationSchema ? validationSchema : baseSchema;

  const methods = useForm({
    resolver: zodResolver(schema),
    mode: "onChange",
  });

  const { handleSubmit, control, formState, watch } = methods;

  const watchedValues = watch();

  useEffect(() => {
    if (setButtonDisabled) {
      setButtonDisabled(formState.isValid ? false : true);
    }
  }, [formState.isValid, setButtonDisabled]);

  return (
    <FormProvider {...methods}>
      <form
        className={`${styles.dynamicForm} ${condensed ? styles.condensed : ""}`}
        id={id}
        onSubmit={handleSubmit(onSubmit)}
      >
        {fields.map((field, index) => {
          if (field.hidden && field.hidden(watchedValues)) return null;
          return (
            <div key={`${field.name}-${index}`}>
              {(field.type === "text" ||
                field.type === "email" ||
                field.type === "password" ||
                field.type === "text-area") && (
                <Controller
                  name={field.name}
                  control={control}
                  render={({ field: controllerField, fieldState }) => (
                    <TextField
                      field={controllerField}
                      label={
                        condensed && fieldState.error?.message
                          ? fieldState.error?.message
                          : field.label
                      }
                      placeholder={field.placeholder}
                      type={field.type as TextFieldType}
                      hasError={!!fieldState.error}
                      helperText={
                        condensed ? "" : fieldState.error?.message || " "
                      }
                      multiline={field.type === "text-area"}
                      focused={
                        !!(field.placeholder && field.label) ||
                        (condensed && !!fieldState.error?.message)
                      }
                    />
                  )}
                />
              )}
              {field.type === "checkbox" && (
                <Controller
                  name={field.name}
                  control={control}
                  defaultValue={false}
                  render={({ field: controllerField }) => {
                    return (
                      <CheckBox
                        checked={!!controllerField.value}
                        toggleChecked={() =>
                          controllerField.onChange(!controllerField.value)
                        }
                        label={field.label}
                        additionalPadding
                      />
                    );
                  }}
                />
              )}
              {field.type === "label" && (
                <p className={`body-medium ${styles.formLabel}`}>
                  {field.label}
                </p>
              )}
              {field.type === "input-group" && (
                <div className={styles.inputGroup}>
                  {field.inputs?.map(
                    (input) =>
                      (input.type === "text" ||
                        input.type === "email" ||
                        input.type === "password" ||
                        input.type === "text-area") && (
                        <Controller
                          key={input.name}
                          name={input.name}
                          control={control}
                          render={({ field: controllerField, fieldState }) => (
                            <TextField
                              field={controllerField}
                              label={
                                condensed && fieldState.error?.message
                                  ? fieldState.error?.message
                                  : input.label
                              }
                              placeholder={input.placeholder}
                              type={input.type as TextFieldType}
                              hasError={!!fieldState.error}
                              helperText={
                                condensed
                                  ? ""
                                  : fieldState.error?.message || " "
                              }
                              multiline={input.type === "text-area"}
                              focused={
                                !!(input.placeholder && input.label) ||
                                (condensed && !!fieldState.error?.message)
                              }
                              flexGrow={input.flexGrow}
                            />
                          )}
                        />
                      ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </form>
    </FormProvider>
  );
}

export default DynamicForm;
